import { getVisibleOoCaseSidebarItemIds } from '@components/CaseSidebar/buildOoCaseSidebar';
import {
  mapOoCaseSidebarItemToUrlSection,
  normalizeOoSectionParam,
} from '@components/CaseSidebar/navigation/ooFormSectionMapping';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

function buildSectionSearch(itemId: CaseSidebarItemId, searchParams?: URLSearchParams): string {
  const params = new URLSearchParams(searchParams);

  params.set('section', mapOoCaseSidebarItemToUrlSection(itemId));

  return `?${params.toString()}`;
}

export function useOoSectionNavigation(context: OoCaseSidebarContext) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formSectionParam = searchParams.get('section');
  const allowedSectionIds = useMemo(() => getVisibleOoCaseSidebarItemIds(context), [context]);

  const updateSectionInUrl = useCallback(
    (itemId: CaseSidebarItemId) => {
      navigate({ search: buildSectionSearch(itemId, searchParams) });
    },
    [navigate, searchParams],
  );

  useEffect(() => {
    const normalizedSection = normalizeOoSectionParam(formSectionParam, context);

    if (!normalizedSection || !allowedSectionIds.includes(normalizedSection)) {
      navigate({ search: buildSectionSearch(allowedSectionIds[0]) }, { replace: true });
      return;
    }

    if (formSectionParam !== mapOoCaseSidebarItemToUrlSection(normalizedSection)) {
      navigate({ search: buildSectionSearch(normalizedSection, searchParams) }, { replace: true });
    }
  }, [allowedSectionIds, context, formSectionParam, navigate, searchParams]);

  return {
    formSectionParam,
    updateSectionInUrl,
  };
}
